import {FC, useEffect, useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import {PageTitle} from '../../../../_metronic/layout/core'
import {GET, POST, PUT, PATCH, DELETE} from '../../../services/api'
import {Loading} from '../../../components/Loading/Loading'
import Swal from 'sweetalert2'
import {TableManagementMap} from './components/TableManagementMap'
import {TableManagementList} from './components/TableManagementList'
import {TableCRUDModal} from './components/TableCRUDModal'
import {BulkTableCreationModal} from './components/BulkTableCreationModal'
import {TableConfigModal} from './components/TableConfigModal'
import {TableDistributionModal} from './components/TableDistributionModal'

type Environment = {
  id: number
  name: string
  tables: number
  seatsAvailable: number
}

type TableType = {
  id: number
  capacity: number
  description: string
}

type Table = {
  id: number
  environmentId: number
  tableTypeId: number
  tableNumber: string
  customCapacity?: number
  positionX: number | null
  positionY: number | null
  isActive: number
  notes: string | null
  tableType?: {
    id: number
    capacity: number
    description: string
  }
  currentStatus?: string
}

type Config = {
  id: number
  environmentId: number
  defaultCleaningTimeMinutes: number
  autoAllocationEnabled: number
  autoReleaseEnabled: number
  allowOverbooking: number
  bufferTimeMinutes: number
  maxTablesPerCombination: number
  maxWastePercentage: number
  preferFewerTables: number
  allowTablesNearby: boolean
}

type BookingHour = {
  id: string
  hour: string
  bookingDayId: string
  bookingConfigId: string
  deletedAt: string | null
}

type StoredFilters = {
  selectedEnvironment: number | null
  filterDate: string
  filterHourId: number | null
}

const STORAGE_KEY = 'table-management-filters'

const saveFiltersToStorage = (filters: StoredFilters) => {
  try {
    const json = JSON.stringify(filters)
    localStorage.setItem(STORAGE_KEY, json)
    console.log('💾 Filtros salvos no localStorage:', json)
  } catch (error) {
    console.warn('⚠️ Erro ao salvar filtros no localStorage:', error)
  }
}

const loadFiltersFromStorage = (): StoredFilters | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    console.log('📂 Tentando carregar do localStorage. Encontrado:', stored)
    if (stored) {
      const parsed = JSON.parse(stored)
      console.log('✅ Filtros carregados do localStorage:', parsed)
      return parsed
    }
    console.log('⚠️ Nenhum filtro salvo no localStorage')
    return null
  } catch (error) {
    console.warn('⚠️ Erro ao carregar/parsear filtros do localStorage:', error)
    return null
  }
}

const TableManagementContent: FC = () => {
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [isReloading, setIsReloading] = useState(false)
  const [environments, setEnvironments] = useState<Environment[]>([])
  const [selectedEnvironment, setSelectedEnvironment] = useState<number | null>(null)
  const [tables, setTables] = useState<Table[]>([])
  const [tableTypes, setTableTypes] = useState<TableType[]>([])
  const [config, setConfig] = useState<Config | undefined>(undefined)
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list')

  // Filtros de data/horário para visualizar alocações
  const [filterDate, setFilterDate] = useState('')
  const [filterHourId, setFilterHourId] = useState<number | null>(null)
  const [bookingHours, setBookingHours] = useState<BookingHour[]>([])
  const [bookingHoursLoaded, setBookingHoursLoaded] = useState(false)
  const [isRestoringFromStorage, setIsRestoringFromStorage] = useState(true)
  const [allocations, setAllocations] = useState<any[]>([])
  const [availableBookings, setAvailableBookings] = useState<any[]>([])

  // Seleção múltipla
  const [selectedTableIds, setSelectedTableIds] = useState<number[]>([])
  const [selectionMode, setSelectionMode] = useState(false)

  // Modais
  const [showTableModal, setShowTableModal] = useState(false)
  const [showBulkModal, setShowBulkModal] = useState(false)
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [showDistributionModal, setShowDistributionModal] = useState(false)
  const [selectedTable, setSelectedTable] = useState<Table | null>(null)
  const [modalAction, setModalAction] = useState<'create' | 'edit'>('create')

  // Carregar filtros salvos do localStorage ao montar
  useEffect(() => {
    console.log('🚀 [table-management.tsx] useEffect MONTAGEM - iniciando...')
    loadEnvironments()
    loadTableTypes()
    loadBookingHours()
    
    // Tenta restaurar filtros do localStorage
    const storedFilters = loadFiltersFromStorage()
    console.log('📋 storedFilters resultado:', storedFilters)
    
    // Verificar parâmetros da URL para filtros pré-aplicados (URL tem prioridade)
    const environmentId = searchParams.get('environmentId')
    console.log('🔗 environmentId da URL:', environmentId)

    if (environmentId) {
      // Prioridade: URL > localStorage > padrão
      try {
        const envId = parseInt(environmentId)
        console.log(`✅ Setando ambiente da URL: ${envId}`)
        setSelectedEnvironment(envId)
      } catch (e) {
        console.error('Erro ao parsear environmentId:', e)
      }
    } else if (storedFilters?.selectedEnvironment) {
      // Se não tem URL, tenta usar localStorage
      console.log(`✅ Setando ambiente do localStorage: ${storedFilters.selectedEnvironment}`)
      setSelectedEnvironment(storedFilters.selectedEnvironment)
    } else {
      console.log('ℹ️ Nenhum ambiente pré-selecionado (URL ou localStorage)')
    }

    // Restaurar data: localStorage > hoje
    if (storedFilters?.filterDate) {
      console.log(`✅ Setando data do localStorage: ${storedFilters.filterDate}`)
      setFilterDate(storedFilters.filterDate)
    } else {
      const today = new Date().toLocaleDateString('en-CA')
      console.log(`✅ Setando data padrão (hoje): ${today}`)
      setFilterDate(today)
    }

    // NÃO restaurar hora aqui - esperar os horários carregarem
    console.log('ℹ️ Restauração de hora será feita após horários carregarem')

    // Limpar sessionStorage da navegação anterior
    sessionStorage.removeItem('pendingTimeSlotFilter')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Salvar filtros no localStorage quando mudarem
  useEffect(() => {
    // 🔴 Gate: Não salvar enquanto estamos restaurando do localStorage
    if (isRestoringFromStorage) {
      console.log('⏳ [AUTOSAVE] Bloqueado durante restauração - isRestoringFromStorage = true')
      return
    }

    // Só salva se pelo menos um filtro foi preenchido
    if (selectedEnvironment || filterDate || filterHourId) {
      console.log('💾 [AUTOSAVE] Salvando filtros mudados:', {
        selectedEnvironment,
        filterDate,
        filterHourId,
      })
      saveFiltersToStorage({
        selectedEnvironment,
        filterDate,
        filterHourId,
      })
    } else {
      console.log('⏭️ Nenhum filtro preenchido, pulando autosave')
    }
  }, [selectedEnvironment, filterDate, filterHourId, isRestoringFromStorage])

  // Restaurar hora do localStorage DEPOIS que os horários carregarem
  useEffect(() => {
    if (!bookingHoursLoaded || bookingHours.length === 0) {
      console.log('⏳ [RESTAURAÇÃO] Aguardando horários carregarem...')
      return
    }

    console.log('✅ [RESTAURAÇÃO] Horários carregados! Tentando restaurar hora do localStorage')
    
    const storedFilters = loadFiltersFromStorage()
    console.log('📂 [RESTAURAÇÃO] Filtros no localStorage:', storedFilters)

    // Se não tem hora restaurada e ainda não tem hora selecionada, usar padrão
    if (storedFilters?.filterHourId) {
      const hourExists = bookingHours.find((h) => Number(h.id) === Number(storedFilters.filterHourId))
      if (hourExists) {
        console.log(`✅ [RESTAURAÇÃO] Hora ${storedFilters.filterHourId} encontrada nos horários, restaurando`)
        setFilterHourId(storedFilters.filterHourId as any)
      } else {
        console.log(`⚠️ [RESTAURAÇÃO] Hora ${storedFilters.filterHourId} NÃO encontrada nos horários carregados`)
        // Se a hora salva não existe mais, usar primeira disponível
        if (!filterHourId && bookingHours.length > 0) {
          console.log(`✅ [RESTAURAÇÃO] Usando primeira hora disponível: ${bookingHours[0].id}`)
          setFilterHourId(bookingHours[0].id as any)
        }
      }
    } else {
      console.log('ℹ️ [RESTAURAÇÃO] Nenhuma hora salva no localStorage')
      // Se não tem hora salva mas tem horários, usar primeira disponível
      if (!filterHourId && bookingHours.length > 0) {
        console.log(`✅ [RESTAURAÇÃO] Usando primeira hora disponível: ${bookingHours[0].id}`)
        setFilterHourId(bookingHours[0].id as any)
      }
    }
    // 🟢 Restauração completa! Desbloquear autosave
    console.log('🟢 [AUTOSAVE] Desbloqueado - setando isRestoringFromStorage = false')
    setIsRestoringFromStorage(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingHoursLoaded, bookingHours])

  useEffect(() => {
    if (selectedEnvironment) {
      // Resetar hora quando muda de ambiente
      setFilterHourId(null)
      loadTables()
      loadConfig()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEnvironment])

  // Resetar hora quando data muda (força seleção da primeira hora disponível)
  useEffect(() => {
    if (filterDate && filterHourId) {
      setFilterHourId(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDate])

  useEffect(() => {
    if (selectedEnvironment && filterDate && filterHourId) {
      loadAllocations()
      loadAvailableBookings()
    } else {
      setAvailableBookings([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEnvironment, filterDate, filterHourId])

  const loadEnvironments = async () => {
    try {
      setLoading(true)
      const res = await GET('/queueReservation/bookingEnvironment')
      console.log('📍 Ambientes carregados do backend:', res.data)
      if (res.success && res.data) {
        // Normalizar IDs para number
        const normalizedEnvironments = res.data.map((env: any) => ({
          ...env,
          id: Number(env.id),
          tables: Number(env.tables) || 0,
          seatsAvailable: Number(env.seatsAvailable) || 0,
        }))
        setEnvironments(normalizedEnvironments)
        console.log(
          '✅ Ambientes setados no state:',
          normalizedEnvironments.map((e: Environment) => ({
            id: e.id,
            name: e.name,
            tables: e.tables,
            seatsAvailable: e.seatsAvailable,
          }))
        )
        if (normalizedEnvironments.length > 0 && !selectedEnvironment) {
          console.log('🎯 Selecionando primeiro ambiente:', normalizedEnvironments[0].id)
          setSelectedEnvironment(normalizedEnvironments[0].id)
        }
      }
      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.error('❌ Erro ao carregar ambientes:', e)
    }
  }

  const loadTableTypes = async () => {
    try {
      console.log('🔄 Carregando tipos de mesa...')
      const res = await GET('/queueReservation/table-management/table-types')
      console.log('📦 Tipos de mesa recebidos:', res)
      if (res.success && res.data) {
        console.log('✅ Tipos de mesa carregados:', res.data.length, 'itens')
        setTableTypes(res.data)
      } else {
        console.warn('⚠️ Nenhum tipo de mesa encontrado')
      }
    } catch (e) {
      console.error('❌ Erro ao carregar tipos de mesa:', e)
    }
  }

  const loadTables = async () => {
    if (!selectedEnvironment) return

    try {
      setLoading(true)
      const res = await GET(
        `/queueReservation/table-management/environments/${selectedEnvironment}/tables`
      )
      if (res.success && res.data) {
        setTables(res.data)
      }
      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.error(e)
    }
  }

  const loadConfig = async () => {
    if (!selectedEnvironment) {
      console.warn('⚠️ loadConfig: selectedEnvironment is null/undefined')
      return
    }

    try {
      console.log(`🔄 [loadConfig] Carregando config para ambiente: ${selectedEnvironment}`)
      const res = await GET(
        `/queueReservation/table-management/environments/${selectedEnvironment}/config`
      )
      console.log('📋 [loadConfig] Resposta da API:', res)
      
      if (res.success && res.data) {
        setConfig(res.data)
        console.log('✅ [loadConfig] Config carregado com sucesso:', res.data)
      } else {
        console.warn('⚠️ [loadConfig] Resposta sem sucesso ou sem dados:', res)
        // Não faz nada, deixa usar o padrão do componente
      }
    } catch (e: any) {
      console.error('❌ [loadConfig] Erro completo:', {
        message: e?.message,
        status: e?.response?.status,
        statusText: e?.response?.statusText,
        data: e?.response?.data,
        config: e?.config,
      })
      // Não faz nada, deixa usar o padrão do componente
    }
  }

  const loadBookingHours = async () => {
    try {
      console.log('⏰ Carregando horários de reserva...')
      const res = await GET('/queueReservation/bookingHour')
      if (res.success && res.data) {
        console.log('📦 Total de horários retornados da API:', res.data.length)
        const activeHours = res.data.filter((hour: any) => !hour.deletedAt)
        console.log('✅ Horários ativos (sem deletedAt):', activeHours.length)
        setBookingHours(activeHours)
        setBookingHoursLoaded(true)
        console.log('✅ Flag bookingHoursLoaded setada como true')
        if (activeHours.length > 0 && !filterHourId) {
          setFilterHourId(activeHours[0].id)
          console.log('🎯 Horário padrão selecionado:', activeHours[0].id, '-', activeHours[0].hour)
        }
      }
    } catch (e) {
      console.error('❌ Erro ao carregar horários:', e)
      setBookingHoursLoaded(true)
    }
  }

  const loadAllocations = async () => {
    console.log('🔍 loadAllocations chamada com:', {selectedEnvironment, filterDate, filterHourId})

    if (!selectedEnvironment || !filterDate || !filterHourId) {
      console.log('⚠️ loadAllocations: retornando early - faltam filtros')
      return
    }

    try {
      const selectedHour = bookingHours.find((h) => Number(h.id) === Number(filterHourId))
      if (!selectedHour) {
        console.log('⚠️ loadAllocations: hora não encontrada no array')
        return
      }

      console.log('📤 loadAllocations: enviando request com:', {
        date: filterDate,
        hour: selectedHour.hour,
        environmentId: selectedEnvironment,
      })

      const res = await POST('/queueReservation/table-management/allocations/filter', {
        date: filterDate,
        hour: selectedHour.hour,
        environmentId: selectedEnvironment,
      })

      console.log('📦 loadAllocations: resposta recebida:', res)
      console.log('📦 loadAllocations: tipo da resposta:', typeof res, Array.isArray(res))
      console.log('📦 loadAllocations: res.length:', res?.length)
      console.log('📦 loadAllocations: res.success:', res?.success)
      console.log('📦 loadAllocations: res.data:', res?.data)

      let allocationsToSet = []
      if (res && res.length > 0) {
        allocationsToSet = res
        console.log('✅ loadAllocations: ', res.length, 'alocações definidas no estado')
        // Log detalhado de cada alocação
        res.forEach((alloc: any, idx: number) => {
          console.log(`📋 Alocação ${idx + 1}:`, {
            tableNumber: alloc.tableNumber,
            allocation_source: alloc.allocation_source,
            clientName: alloc.clientName,
            people: alloc.people,
            phone: alloc.phone,
            bookingId: alloc.bookingId,
            queueId: alloc.queueId,
            queueDateAllocation: alloc.queueDateAllocation,
          })
        })
      } else if (res?.success && res?.data && res.data.length > 0) {
        // Caso a resposta venha no formato {success: true, data: [...]}
        allocationsToSet = res.data
        console.log(
          '✅ loadAllocations (formato data): ',
          res.data.length,
          'alocações definidas no estado'
        )
        // Log detalhado de cada alocação
        res.data.forEach((alloc: any, idx: number) => {
          console.log(`📋 Alocação ${idx + 1}:`, {
            tableNumber: alloc.tableNumber,
            allocation_source: alloc.allocation_source,
            clientName: alloc.clientName,
            people: alloc.people,
            phone: alloc.phone,
            bookingId: alloc.bookingId,
            queueId: alloc.queueId,
            queueDateAllocation: alloc.queueDateAllocation,
          })
        })
      } else {
        allocationsToSet = []
        console.log('⚠️ loadAllocations: nenhuma alocação encontrada, limpando estado')
      }

      // ⭐ LOG CRÍTICO: Mostrar EXATAMENTE o que vai ser setado
      console.log('🔵 === ANTES DE setAllocations ===');
      console.log(`Total de allocations a setar: ${allocationsToSet.length}`);
      allocationsToSet.forEach((alloc: any, idx: number) => {
        console.log(
          `  [${idx}] ${alloc.tableNumber}: bookingId=${alloc.bookingId}, queueId=${alloc.queueId}, source=${alloc.allocation_source}`
        );
      });
      console.log('🔵 === FIM ANTES DE setAllocations ===');

      setAllocations(allocationsToSet)
    } catch (e) {
      console.error('❌ loadAllocations: erro:', e)
      setAllocations([])
    }
  }

  const loadAvailableBookings = async () => {
    if (!selectedEnvironment || !filterDate || !filterHourId) {
      console.log('⚠️ Filtros incompletos para buscar reservas:', {
        selectedEnvironment,
        filterDate,
        filterHourId,
      })
      setAvailableBookings([])
      return
    }

    try {
      const selectedHour = bookingHours.find((h) => Number(h.id) === Number(filterHourId))
      if (!selectedHour) {
        setAvailableBookings([])
        return
      }

      const requestPayload = {
        date: filterDate,
        hour: selectedHour.hour,
        bookingEnvironmentId: selectedEnvironment,
      }

      const res = await POST('/queueReservation/booking/filter-for-distribution', requestPayload)
      console.log('📦 RESPOSTA DA API:')
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━')
      console.log('Success:', res.success)
      console.log('Data:', res.data)

      if (res.success && res.data) {
        setAvailableBookings(res.data)
      } else {
        setAvailableBookings([])
      }
    } catch (e) {
      setAvailableBookings([])
    }
  }

  const handleReloadData = async () => {
    if (!selectedEnvironment || !filterDate || !filterHourId) {
      Swal.fire({
        icon: 'warning',
        title: 'Filtros Incompletos',
        text: 'Selecione um Ambiente, Data e Horário antes de recarregar',
      })
      return
    }

    setIsReloading(true)
    try {
      // Recarregar as mesas e alocações com os filtros atuais
      await loadTables()
      await loadAllocations()
      await loadAvailableBookings()
      
      Swal.fire({
        icon: 'success',
        title: 'Recarregado!',
        text: 'Dados attualizados com sucesso',
        timer: 2000,
        showConfirmButton: false,
      })
    } catch (error) {
      console.error('Erro ao recarregar dados:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao recarregar os dados',
      })
    } finally {
      setIsReloading(false)
    }
  }

  const handleAllocateBooking = async (tableId: number, bookingId: number) => {
    try {
      console.log('🎯 Alocando:', {tableId, bookingId})

      const res = await POST('/queueReservation/table-management/allocations/manual', {
        bookingId: bookingId,
        environmentTableIds: [tableId], // Array com apenas uma mesa
        userId: 1, // TODO: pegar do contexto do usuário logado
      })

      console.log('✅ Resposta da alocação:', res)

      Swal.fire({
        icon: 'success',
        title: 'Reserva alocada com sucesso!',
        showConfirmButton: false,
        timer: 1500,
      })

      // Aguardar 300ms para garantir que o banco finalizou
      await new Promise((resolve) => setTimeout(resolve, 300))

      // Recarregar dados
      await loadAllocations()
      await loadAvailableBookings()

      console.log('🔄 Dados recarregados após alocação')
    } catch (error: any) {
      console.error('❌ Erro ao alocar reserva:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro ao alocar reserva',
        text: error.response?.data?.message || 'Tente novamente mais tarde',
      })
    }
  }

  const handleAllocateBookingMultiple = async (tableIds: number[], bookingId: number) => {
    try {
      console.log('🎯 Alocando em múltiplas mesas:', { tableIds, bookingId })

      const res = await POST('/queueReservation/table-management/allocations/manual', {
        bookingId: bookingId,
        environmentTableIds: tableIds, // Array com múltiplas mesas
        userId: 1, // TODO: pegar do contexto do usuário logado
      })

      console.log('✅ Resposta da alocação múltipla:', res)

      Swal.fire({
        icon: 'success',
        title: 'Reserva alocada com sucesso!',
        text: `Alocada em ${tableIds.length} mesa(s)`,
        showConfirmButton: false,
        timer: 1500,
      })

      // Aguardar 300ms para garantir que o banco finalizou
      await new Promise((resolve) => setTimeout(resolve, 300))

      // Recarregar dados
      await loadAllocations()
      await loadAvailableBookings()

      console.log('🔄 Dados recarregados após alocação múltipla')
    } catch (error: any) {
      console.error('❌ Erro ao alocar reserva em múltiplas mesas:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro ao alocar reserva',
        text: error.response?.data?.message || 'Tente novamente mais tarde',
      })
    }
  }

  const handleDeallocateBooking = async (allocationId: number) => {
    try {
      await DELETE(`/queueReservation/table-management/allocations/${allocationId}`)

      console.log('✅ Alocação deletada, recarregando dados...')

      // Aguardar 500ms para garantir que o banco finalizou a transação
      // (aumentado de 300ms para evitar race conditions com modal)
      await new Promise((resolve) => setTimeout(resolve, 500))

      // Recarregar mesas (para atualizar status visual)
      await loadTables()
      // Recarregar alocações
      await loadAllocations()
      await loadAvailableBookings()

      console.log('✅✅ SINCRONIZAÇÃO COMPLETA: Dados atualizados com nova alocação state')

      Swal.fire({
        icon: 'success',
        title: 'Reserva desalocada com sucesso!',
        showConfirmButton: false,
        timer: 1500,
      })
    } catch (error) {
      console.error('Erro ao desalocar reserva:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro ao desalocar reserva',
        text: 'Tente novamente mais tarde',
      })
    }
  }

  const handleChangeAllocationStatus = async (
    allocationId: number,
    newStatus: 'allocated' | 'occupied' | 'cleaning'
  ) => {
    try {
      console.log('🔄 Alterando status da alocação:', {allocationId, newStatus})

      await PATCH(`/queueReservation/table-management/allocations/${allocationId}/status`, {
        status: newStatus,
        userId: 1, // TODO: pegar do contexto do usuário logado
      })

      console.log('✅ Status alterado, recarregando dados...')

      // Aguardar 500ms para garantir que o banco finalizou
      // (aumentado de 300ms para evitar race conditions com modal)
      await new Promise((resolve) => setTimeout(resolve, 500))

      // Recarregar alocações para refletir novo status
      await loadAllocations()

      const statusText =
        newStatus === 'occupied' ? 'Ocupada' : newStatus === 'cleaning' ? 'Em Limpeza' : 'Alocada'

      console.log('✅✅ SINCRONIZAÇÃO COMPLETA: Dados atualizados com novo status')

      Swal.fire({
        icon: 'success',
        title: `Mesa marcada como ${statusText}!`,
        showConfirmButton: false,
        timer: 1500,
      })
    } catch (error: any) {
      console.error('❌ Erro ao alterar status:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro ao alterar status',
        text: error.response?.data?.message || 'Tente novamente mais tarde',
      })
    }
  }

  // Buscar alocações relacionadas da mesma reserva
  const handleFetchRelatedAllocations = async (
    bookingId: number,
    environmentId: number,
    date?: string,
    hour?: string,
    queueId?: number
  ): Promise<any[]> => {
    try {
      console.log('🔍 Buscando alocações relacionadas:', { bookingId, queueId })

      // Construir URL com query params apropriados
      let url = `/queueReservation/table-management/bookings/${bookingId}/allocations`
      if (queueId && queueId > 0) {
        url += `?queueId=${queueId}`
        console.log('📍 Usando queueId para busca de fila:', queueId)
      }

      // Usar endpoint que retorna todas as alocações de uma reserva ou fila específica
      const response = await GET(url)
      
      console.log('✅ Alocações relacionadas encontradas:', response.data)
      return response.data || []
    } catch (error: any) {
      console.error('❌ Erro ao buscar alocações relacionadas:', error)
      // Retornar array vazio em caso de erro para não quebrar a UI
      return []
    }
  }

  // Alterar status de múltiplas alocações
  const handleChangeStatusBulk = async (
    allocationIds: number[],
    newStatus: 'allocated' | 'occupied' | 'cleaning'
  ) => {
    try {
      console.log('🔄 Alterando status de múltiplas alocações:', {allocationIds, newStatus})

      // Executar alteração para cada alocação (sem batch endpoint existe por enquanto)
      const promises = allocationIds.map(id =>
        PATCH(`/queueReservation/table-management/allocations/${id}/status`, {
          status: newStatus,
          userId: 1, // TODO: pegar do contexto do usuário logado
        })
      )

      await Promise.all(promises)

      console.log('✅ Status alterado em todas as alocações, recarregando dados...')

      // Aguardar 300ms para garantir que o banco finalizou
      await new Promise((resolve) => setTimeout(resolve, 300))

      // Recarregar alocações
      await loadAllocations()

      const statusText =
        newStatus === 'occupied' ? 'Ocupada' : newStatus === 'cleaning' ? 'Em Limpeza' : 'Alocada'

      Swal.fire({
        icon: 'success',
        title: `${allocationIds.length} mesa(s) marcada(s) como ${statusText}!`,
        showConfirmButton: false,
        timer: 1500,
      })
    } catch (error: any) {
      console.error('❌ Erro ao alterar status em lote:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro ao alterar status',
        text: error.response?.data?.message || 'Tente novamente mais tarde',
      })
    }
  }

  // Desalocar múltiplas alocações
  const handleDeallocateBulk = async (allocationIds: number[]) => {
    try {
      console.log('🗑️ Desalocando múltiplas alocações:', allocationIds)

      // Executar deleção para cada alocação
      const promises = allocationIds.map(id =>
        DELETE(`/queueReservation/table-management/allocations/${id}`)
      )

      await Promise.all(promises)

      console.log('✅ Alocações deletadas, recarregando dados...')

      // Aguardar 300ms para garantir que o banco finalizou
      await new Promise((resolve) => setTimeout(resolve, 300))

      // Recarregar tudo
      await loadTables()
      await loadAllocations()
      await loadAvailableBookings()

      Swal.fire({
        icon: 'success',
        title: `${allocationIds.length} mesa(s) desalocada(s) com sucesso!`,
        showConfirmButton: false,
        timer: 1500,
      })
    } catch (error: any) {
      console.error('❌ Erro ao desalocar múltiplas alocações:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro ao desalocar mesas',
        text: error.response?.data?.message || 'Tente novamente mais tarde',
      })
    }
  }

  const handleAllocateWalkIn = async (
    tableIds: number[],
    people: number,
    name: string,
    phone: string,
    email: string
  ) => {
    try {
      console.log('🚶 Alocando walk-in:', {
        tableIds,
        people,
        name,
        phone,
        email,
        date: filterDate,
        hourId: filterHourId,
        environmentId: selectedEnvironment,
      })

      // Validações detalhadas
      if (!filterDate) {
        throw new Error('⚠️ Por favor, selecione uma DATA antes de alocar walk-in')
      }
      if (!filterHourId) {
        throw new Error('⚠️ Por favor, selecione um HORÁRIO antes de alocar walk-in')
      }
      if (!selectedEnvironment) {
        throw new Error('⚠️ Por favor, selecione um AMBIENTE antes de alocar walk-in')
      }

      // Buscar informações do horário para verificar se existe
      console.log('🔍 Verificando horário ID:', filterHourId, '(tipo:', typeof filterHourId, ')')
      console.log('📋 Total de horários no array:', bookingHours.length)
      console.log('🔢 IDs disponíveis:', bookingHours.map((h) => `${h.id} (${h.hour})`).join(', '))

      // Comparar com conversão de tipo (pode ser string vs number)
      const hourInfo = bookingHours.find((h) => Number(h.id) === Number(filterHourId))

      if (!hourInfo) {
        const availableIds = bookingHours.map((h) => h.id).join(', ')
        throw new Error(
          `⚠️ Horário ID ${filterHourId} não encontrado no array de ${bookingHours.length} horários carregados.\n\nIDs disponíveis: [${availableIds}]\n\nRecarregue a página e tente novamente.`
        )
      }
      console.log('✅ Horário encontrado:', hourInfo)
      console.log('🔑 bookingConfigId do horário:', hourInfo.bookingConfigId)

      // Criar reserva temporária para walk-in
      console.log('📝 Criando reserva temporária...')
      const bookingPayload = {
        name: name,
        people: people,
        date: filterDate,
        bookingHourId: Number(filterHourId), // ✅ CORRIGIDO: Converter para número
        bookingConfigId: Number(hourInfo.bookingConfigId), // ✅ ADICIONADO: Campo obrigatório para validação backend
        bookingEnvironmentId: selectedEnvironment,
        phone: phone || 'Walk-in',
        email: email || null,
        observation: `Walk-in criado automaticamente - ${
          tableIds.length
        } mesa(s) - ${new Date().toLocaleString('pt-BR')}`,
      }
      console.log('📤 Payload da reserva:', bookingPayload)
      console.log(
        '🔍 bookingHourId tipo:',
        typeof bookingPayload.bookingHourId,
        '| valor:',
        bookingPayload.bookingHourId
      )
      console.log(
        '🔍 bookingConfigId tipo:',
        typeof bookingPayload.bookingConfigId,
        '| valor:',
        bookingPayload.bookingConfigId
      )

      console.log('🚀 Enviando requisição para backend...')
      console.log('🔍 WALK-IN MODE: Enviando com hour string como alternativa')

      // Para walk-in, vamos enviar TAMBÉM a hora como string para o backend poder usar
      const walkInPayload = {
        ...bookingPayload,
        hour: hourInfo.hour, // Adicionar hora como string (ex: "01:00")
        isWalkIn: true, // Flag para identificar walk-in
      }
      console.log('📤 Payload WALK-IN completo:', walkInPayload)

      const res = await POST('/queueReservation/booking', walkInPayload)

      console.log('📋 Resposta COMPLETA da criação de reserva:', res)
      console.log('📋 res.success:', res.success)
      console.log('📋 res.data:', res.data)
      console.log('📋 res.message:', res.message)
      console.log('📋 res.error:', res.error)

      if (res.success && res.data?.id) {
        const bookingId = res.data.id
        console.log('✅ Reserva criada com ID:', bookingId)

        // Alocar nas mesas especificadas
        console.log('🎯 Alocando nas mesas:', tableIds)
        const allocPayload = {
          bookingId: bookingId,
          environmentTableIds: tableIds,
          userId: 1, // TODO: pegar do contexto
        }
        console.log('📤 Payload da alocação manual:', allocPayload)

        const allocRes = await POST(
          '/queueReservation/table-management/allocations/manual',
          allocPayload
        )

        console.log('📋 Resposta COMPLETA da alocação:', allocRes)
        console.log('📋 allocRes.success:', allocRes?.success)
        console.log('📋 allocRes.data:', allocRes?.data)
        console.log('📋 allocRes.message:', allocRes?.message)

        await new Promise((resolve) => setTimeout(resolve, 300))

        console.log('🔄 Recarregando dados com filtros:', {
          selectedEnvironment,
          filterDate,
          filterHourId,
          hasBookingHours: bookingHours.length > 0,
        })

        await loadTables()
        await loadAllocations()
        await loadAvailableBookings()

        Swal.fire({
          icon: 'success',
          title: 'Walk-in alocado com sucesso!',
          html: `
            <strong>${name}</strong> - ${people} pessoa(s)<br/>
            <small>Mesa(s): ${
              tableIds.length > 1 ? tableIds.length + ' mesas unidas' : '1 mesa'
            }</small>
          `,
          showConfirmButton: false,
          timer: 2500,
        })
      } else {
        throw new Error(res.message || 'Erro ao criar reserva walk-in')
      }
    } catch (error: any) {
      console.error('❌ Erro ao alocar walk-in:', error)
      console.error('📋 Detalhes do erro:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      })

      Swal.fire({
        icon: 'error',
        title: 'Erro ao alocar walk-in',
        html: `
          <strong>${
            error.response?.data?.message || error.message || 'Erro desconhecido'
          }</strong><br/>
          <small class="text-muted">Verifique se data, horário e ambiente estão selecionados</small>
        `,
        confirmButtonText: 'Entendi',
      })

      throw error // Re-lançar para que o modal saiba que houve erro
    }
  }

  const handleCreateTable = () => {
    const currentEnv = environments.find((e) => e.id === selectedEnvironment)
    const maxTables = currentEnv?.tables || 0
    const maxSeats = currentEnv?.seatsAvailable || 0
    const currentTables = tables.length
    const currentSeats = tables.reduce(
      (sum, t) => sum + (t.customCapacity || t.tableType?.capacity || 0),
      0
    )

    console.log('🆕 Nova Mesa clicada', {
      selectedEnvironment,
      tableTypesCount: tableTypes.length,
      currentTables,
      maxTables,
      currentSeats,
      maxSeats,
    })

    // Validação 1: Limite de mesas
    if (maxTables > 0 && currentTables >= maxTables) {
      Swal.fire({
        icon: 'warning',
        title: 'Limite de mesas atingido',
        html: `Este ambiente suporta no máximo <b>${maxTables}</b> mesa(s).<br>Atualmente existem <b>${currentTables}</b> mesa(s) cadastrada(s).`,
        confirmButtonText: 'OK',
      })
      return
    }

    // Validação 2: Capacidade total (será validada no backend também)
    // Aqui só mostramos um aviso se já estiver próximo do limite
    if (maxSeats > 0 && currentSeats >= maxSeats) {
      Swal.fire({
        icon: 'warning',
        title: 'Capacidade máxima atingida',
        html: `Este ambiente suporta no máximo <b>${maxSeats}</b> lugares.<br>Atualmente há <b>${currentSeats}</b> lugares cadastrados.<br><br>Não é possível adicionar mais mesas.`,
        confirmButtonText: 'OK',
      })
      return
    }

    setSelectedTable(null)
    setModalAction('create')
    setShowTableModal(true)
  }

  const handleEditTable = (table: Table) => {
    setSelectedTable(table)
    setModalAction('edit')
    setShowTableModal(true)
  }

  const handleDeleteTable = async (tableId: number) => {
    const result = await Swal.fire({
      title: 'Confirmar exclusão',
      text: 'Deseja realmente excluir esta mesa?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
    })

    if (result.isConfirmed) {
      try {
        const res = await DELETE(`/queueReservation/table-management/environment-tables/${tableId}`)
        if (res.success) {
          Swal.fire('Sucesso!', 'Mesa excluída com sucesso', 'success')
          loadTables()
        }
      } catch (e) {
        Swal.fire('Erro!', 'Erro ao excluir mesa', 'error')
      }
    }
  }

  const toggleTableSelection = (tableId: number) => {
    setSelectedTableIds((prev) =>
      prev.includes(tableId) ? prev.filter((id) => id !== tableId) : [...prev, tableId]
    )
  }

  const toggleSelectAll = () => {
    if (selectedTableIds.length === tables.length) {
      setSelectedTableIds([])
    } else {
      setSelectedTableIds(tables.map((t) => t.id))
    }
  }

  const handleBulkActivateSelected = async (isActive: boolean) => {
    if (selectedTableIds.length === 0) {
      Swal.fire('Atenção', 'Nenhuma mesa selecionada', 'warning')
      return
    }

    const action = isActive ? 'ativar' : 'desativar'
    const selectedTables = tables.filter((t) => selectedTableIds.includes(t.id))
    const tableNumbers = selectedTables.map((t) => t.tableNumber).join(', ')

    const result = await Swal.fire({
      title: `${isActive ? 'Ativar' : 'Desativar'} Mesas Selecionadas`,
      html: `Deseja <strong>${action}</strong> ${selectedTableIds.length} mesa(s)?<br><br><small class="text-muted">Mesas: ${tableNumbers}</small>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: `Sim, ${action}`,
      cancelButtonText: 'Cancelar',
      confirmButtonColor: isActive ? '#50cd89' : '#f1416c',
    })

    if (result.isConfirmed) {
      try {
        setLoading(true)
        let successCount = 0
        let errorCount = 0

        for (const table of selectedTables) {
          try {
            await PUT(`/queueReservation/table-management/environment-tables/${table.id}`, {
              ...table,
              isActive: isActive ? 1 : 0,
            })
            successCount++
          } catch (e) {
            errorCount++
          }
        }

        setLoading(false)

        if (errorCount === 0) {
          Swal.fire(
            'Sucesso!',
            `${successCount} mesa(s) ${isActive ? 'ativada(s)' : 'desativada(s)'} com sucesso`,
            'success'
          )
        } else {
          Swal.fire(
            'Parcialmente Concluído',
            `${successCount} mesa(s) ${
              isActive ? 'ativada(s)' : 'desativada(s)'
            }. ${errorCount} erro(s).`,
            'warning'
          )
        }

        setSelectedTableIds([])
        setSelectionMode(false)
        loadTables()
      } catch (e) {
        setLoading(false)
        Swal.fire('Erro!', `Erro ao ${action} mesas`, 'error')
      }
    }
  }

  const handleBulkActivate = async (isActive: boolean) => {
    const action = isActive ? 'ativar' : 'desativar'
    const result = await Swal.fire({
      title: `${isActive ? 'Ativar' : 'Desativar'} Todas as Mesas`,
      html: `Deseja <strong>${action}</strong> todas as ${tables.length} mesa(s) do ambiente <strong>${selectedEnvName}</strong>?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: `Sim, ${action}`,
      cancelButtonText: 'Cancelar',
      confirmButtonColor: isActive ? '#50cd89' : '#f1416c',
    })

    if (result.isConfirmed) {
      try {
        setLoading(true)
        let successCount = 0
        let errorCount = 0

        for (const table of tables) {
          try {
            await PUT(`/queueReservation/table-management/environment-tables/${table.id}`, {
              ...table,
              isActive: isActive ? 1 : 0,
            })
            successCount++
          } catch (e) {
            errorCount++
          }
        }

        setLoading(false)

        if (errorCount === 0) {
          Swal.fire(
            'Sucesso!',
            `${successCount} mesa(s) ${isActive ? 'ativada(s)' : 'desativada(s)'} com sucesso`,
            'success'
          )
        } else {
          Swal.fire(
            'Parcialmente Concluído',
            `${successCount} mesa(s) ${
              isActive ? 'ativada(s)' : 'desativada(s)'
            }. ${errorCount} erro(s).`,
            'warning'
          )
        }

        loadTables()
      } catch (e) {
        setLoading(false)
        Swal.fire('Erro!', `Erro ao ${action} mesas`, 'error')
      }
    }
  }

  const handleBulkDelete = async () => {
    const result = await Swal.fire({
      title: 'Excluir Todas as Mesas',
      html: `<strong>ATENÇÃO:</strong> Esta ação é irreversível!<br><br>Deseja excluir todas as ${tables.length} mesa(s) do ambiente <strong>${selectedEnvName}</strong>?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir todas',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#f1416c',
    })

    if (result.isConfirmed) {
      try {
        setLoading(true)
        let successCount = 0
        let errorCount = 0

        for (const table of tables) {
          try {
            await DELETE(`/queueReservation/table-management/environment-tables/${table.id}`)
            successCount++
          } catch (e) {
            errorCount++
          }
        }

        setLoading(false)

        if (errorCount === 0) {
          Swal.fire('Sucesso!', `${successCount} mesa(s) excluída(s) com sucesso`, 'success')
        } else {
          Swal.fire(
            'Parcialmente Concluído',
            `${successCount} mesa(s) excluída(s). ${errorCount} erro(s).`,
            'warning'
          )
        }

        loadTables()
      } catch (e) {
        setLoading(false)
        Swal.fire('Erro!', 'Erro ao excluir mesas', 'error')
      }
    }
  }

  const handleSaveTable = async (tableData: any) => {
    try {
      setLoading(true)
      let res

      if (modalAction === 'create') {
        res = await POST(
          `/queueReservation/table-management/environments/${selectedEnvironment}/tables`,
          tableData
        )
      } else {
        res = await PUT(
          `/queueReservation/table-management/environment-tables/${selectedTable?.id}`,
          tableData
        )
      }

      if (res.success) {
        Swal.fire(
          'Sucesso!',
          modalAction === 'create' ? 'Mesa criada com sucesso' : 'Mesa atualizada com sucesso',
          'success'
        )
        setShowTableModal(false)
        loadTables()
      }
      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire('Erro!', e.response?.data?.message || 'Erro ao salvar mesa', 'error')
    }
  }

  const handleSaveBulk = async (bulkTables: any[]) => {
    try {
      setLoading(true)
      const res = await POST(
        `/queueReservation/table-management/environments/${selectedEnvironment}/tables/bulk`,
        {tables: bulkTables}
      )

      if (res.success) {
        Swal.fire('Sucesso!', res.message || 'Mesas criadas com sucesso', 'success')
        setShowBulkModal(false)
        loadTables()
      }
      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire('Erro!', e.response?.data?.message || 'Erro ao criar mesas em lote', 'error')
    }
  }

  const handleSaveConfig = async (configData: any) => {
    try {
      setLoading(true)
      const res = await POST(
        `/queueReservation/table-management/environments/${selectedEnvironment}/config`,
        configData
      )

      if (res.success) {
        Swal.fire('Sucesso!', 'Configuração salva com sucesso', 'success')
        setShowConfigModal(false)
        loadConfig()
      }
      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire('Erro!', e.response?.data?.message || 'Erro ao salvar configuração', 'error')
    }
  }

  const handleUpdateTablePosition = async (tableId: number, x: number, y: number) => {
    try {
      // Atualização otimista: atualizar estado local imediatamente
      setTables((prevTables) =>
        prevTables.map((table) =>
          table.id === tableId ? {...table, positionX: x, positionY: y} : table
        )
      )

      // Salvar no backend
      const response = await PATCH(
        `/queueReservation/table-management/environment-tables/${tableId}/position`,
        {positionX: x, positionY: y}
      )

      // Se o backend retornar sucesso, tudo ok
      if (response.success) {
        console.log('✅ Posição salva:', {tableId, x, y})
      }
    } catch (e: any) {
      console.error('❌ Erro ao atualizar posição:', e)
      // Em caso de erro, recarregar do backend para reverter
      Swal.fire('Erro!', 'Erro ao salvar posição da mesa', 'error')
      loadTables()
    }
  }

  const handleAutoOrganize = async () => {
    try {
      // v2.0 - Fixed grid alignment
      const result = await Swal.fire({
        title: 'Auto-organizar Mesas',
        html: 'Deseja reorganizar todas as mesas em uma grade uniforme?<br><small class="text-muted">As posições atuais serão substituídas.</small>',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, organizar',
        cancelButtonText: 'Cancelar',
      })

      if (!result.isConfirmed) return

      setLoading(true)

      // Calcular posições em grade
      const TABLE_SIZE = 80
      const SPACING = 30
      const MARGIN = 40
      const GRID_SIZE = 20

      // Função para alinhar ao grid
      const snapToGrid = (value: number): number => {
        return Math.round(value / GRID_SIZE) * GRID_SIZE
      }

      const cols = Math.floor((1400 - MARGIN * 2) / (TABLE_SIZE + SPACING))

      // Atualizar cada mesa
      for (let i = 0; i < tables.length; i++) {
        const row = Math.floor(i / cols)
        const col = i % cols

        const x = snapToGrid(MARGIN + col * (TABLE_SIZE + SPACING))
        const y = snapToGrid(MARGIN + row * (TABLE_SIZE + SPACING))

        await PATCH(
          `/queueReservation/table-management/environment-tables/${tables[i].id}/position`,
          {positionX: x, positionY: y}
        )
      }

      await loadTables()
      setLoading(false)

      Swal.fire('Sucesso!', 'Mesas organizadas automaticamente', 'success')
    } catch (e: any) {
      setLoading(false)
      Swal.fire('Erro!', 'Erro ao organizar mesas', 'error')
    }
  }

  const selectedEnvName =
    environments.find((env) => env.id === selectedEnvironment)?.name || 'Ambiente'

  return (
    <>
      <PageTitle breadcrumbs={[]}>Gestão de Mesas</PageTitle>

      <div className='card'>
        <div className='card-header border-0 pt-6'>
          <div className='card-title'>
            <div className='d-flex align-items-center gap-3 flex-wrap'>
              <div className='d-flex align-items-center gap-2'>
                <label className='form-label mb-0 fw-bold'>Ambiente:</label>
                <select
                  className='form-select form-select-sm'
                  style={{width: '200px'}}
                  value={selectedEnvironment || ''}
                  onChange={(e) => setSelectedEnvironment(Number(e.target.value))}
                >
                  {environments.map((env) => (
                    <option key={env.id} value={env.id}>
                      {env.name}
                    </option>
                  ))}
                </select>
              </div>

              {selectedEnvironment &&
                (() => {
                  const currentEnv = environments.find((e) => e.id === selectedEnvironment)

                  // Debug: verificar se está encontrando o ambiente
                  if (!currentEnv) {
                    console.warn('⚠️ Ambiente não encontrado:', {
                      selectedEnvironment,
                      selectedEnvType: typeof selectedEnvironment,
                      environments: environments.map((e) => ({
                        id: e.id,
                        idType: typeof e.id,
                        name: e.name,
                        tables: e.tables,
                        seatsAvailable: e.seatsAvailable,
                      })),
                    })
                  }

                  const maxTables = currentEnv?.tables || 0
                  const maxSeats = currentEnv?.seatsAvailable || 0
                  const totalSeats = tables.reduce(
                    (sum, t) => sum + (t.customCapacity || t.tableType?.capacity || 0),
                    0
                  )

                  return (
                    <>
                      <div className='badge badge-light-primary fs-6'>
                        Mesas: {tables.length} / {maxTables > 0 ? maxTables : '∞'}
                      </div>
                      <div className='badge badge-light-success fs-6'>
                        Lugares: {totalSeats} / {maxSeats > 0 ? maxSeats : '∞'}
                      </div>
                    </>
                  )
                })()}

              <div className='vr'></div>

              <div className='d-flex align-items-center gap-2'>
                <label className='form-label mb-0 fw-bold'>Data:</label>
                <input
                  type='date'
                  className='form-control form-control-sm'
                  style={{width: '150px'}}
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                />
              </div>

              <div className='d-flex align-items-center gap-2'>
                <label className='form-label mb-0 fw-bold'>Horário:</label>
                <select
                  className='form-select form-select-sm'
                  style={{width: '120px'}}
                  value={filterHourId || ''}
                  onChange={(e) => setFilterHourId(Number(e.target.value))}
                >
                  {bookingHours.length === 0 ? (
                    <option value=''>Nenhum horário</option>
                  ) : (
                    bookingHours.map((hour) => (
                      <option key={hour.id} value={hour.id}>
                        {hour.hour}
                      </option>
                    ))
                  )}
                </select>
              </div>

              {allocations.length > 0 && (
                <div className='badge badge-light-success fs-6'>
                  <i className='bi bi-check-circle me-1'></i>
                  {allocations.length} alocações
                </div>
              )}
            </div>
          </div>

          {selectedEnvironment && (
            <div className='card-toolbar'>
              <div className='d-flex gap-2'>
                <div className='btn-group-sm d-flex gap-2' role='group'>
                  <button
                    type='button'
                    className='btn btn-sm btn-light'
                    onClick={() => setViewMode(viewMode === 'list' ? 'map' : 'list')}
                  >
                    <i className={`bi bi-${viewMode === 'list' ? 'map' : 'list-ul'} me-2`}></i>
                    {viewMode === 'list' ? 'Ver Mapa' : 'Ver Lista'}
                  </button>
                  {viewMode === 'map' && tables.length > 0 && (
                    <button
                      type='button'
                      className='btn btn-sm btn-info'
                      onClick={handleAutoOrganize}
                      title='Organizar mesas automaticamente'
                    >
                      <i className='bi bi-grid-3x3 me-2'></i>
                      Auto-organizar
                    </button>
                  )}
                  <button
                    type='button'
                    className='btn btn-sm btn-light'
                    onClick={() => {
                      console.log('🔧 [Botão Configurações] Clicado!')
                      console.log('📊 Estado atual:', {
                        showConfigModal: showConfigModal,
                        config: config,
                        selectedEnvironment: selectedEnvironment,
                      })
                      setShowConfigModal(true)
                    }}
                  >
                    <i className='bi bi-gear me-2'></i>
                    Configurações
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-warning'
                    onClick={() => setShowDistributionModal(true)}
                    title='Distribuir reservas automaticamente'
                  >
                    <i className='bi bi-magic me-2'></i>
                    Distribuição
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-primary'
                    onClick={handleCreateTable}
                  >
                    <i className='bi bi-plus-circle me-2'></i>
                    Nova Mesa
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-success'
                    onClick={() => setShowBulkModal(true)}
                  >
                    <i className='bi bi-stack me-2'></i>
                    Criar em Lote
                  </button>
                </div>
                <div className='d-flex gap-2'>
                  {selectionMode ? (
                    <>
                      <button
                        type='button'
                        className='btn btn-sm btn-light-success'
                        onClick={() => handleBulkActivateSelected(true)}
                        disabled={selectedTableIds.length === 0}
                      >
                        <i className='bi bi-check-circle me-1'></i>
                        Ativar ({selectedTableIds.length})
                      </button>
                      <button
                        type='button'
                        className='btn btn-sm btn-light-danger'
                        onClick={() => handleBulkActivateSelected(false)}
                        disabled={selectedTableIds.length === 0}
                      >
                        <i className='bi bi-x-circle me-1'></i>
                        Desativar ({selectedTableIds.length})
                      </button>
                      <button
                        type='button'
                        className='btn btn-sm btn-light'
                        onClick={() => {
                          setSelectionMode(false)
                          setSelectedTableIds([])
                        }}
                      >
                        <i className='bi bi-x-lg me-1'></i>
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <div className='btn-group'>
                      <button
                        type='button'
                        className='btn btn-sm btn-light-primary dropdown-toggle'
                        data-bs-toggle='dropdown'
                        aria-expanded='false'
                      >
                        <i className='bi bi-lightning-fill me-1'></i>
                        Ações em Lote
                      </button>
                      <ul className='dropdown-menu'>
                        <li>
                          <button className='dropdown-item' onClick={() => setSelectionMode(true)}>
                            <i className='bi bi-check2-square text-primary me-2'></i>
                            Selecionar Mesas
                          </button>
                        </li>
                        <li>
                          <hr className='dropdown-divider' />
                        </li>
                        <li>
                          <button
                            className='dropdown-item'
                            onClick={() => handleBulkActivate(true)}
                          >
                            <i className='bi bi-check-circle text-success me-2'></i>
                            Ativar Todas
                          </button>
                        </li>
                        <li>
                          <button
                            className='dropdown-item'
                            onClick={() => handleBulkActivate(false)}
                          >
                            <i className='bi bi-x-circle text-danger me-2'></i>
                            Desativar Todas
                          </button>
                        </li>
                        <li>
                          <hr className='dropdown-divider' />
                        </li>
                        <li>
                          <button className='dropdown-item text-danger' onClick={handleBulkDelete}>
                            <i className='bi bi-trash me-2'></i>
                            Excluir Todas
                          </button>
                        </li>
                      </ul>
                    </div>
                  )}
                  <button
                    type='button'
                    className='btn btn-sm btn-light-info'
                    onClick={handleReloadData}
                    disabled={isReloading}
                    title='Recarregar com os filtros atuais'
                  >
                    <i className={`bi ${isReloading ? 'bi-arrow-repeat' : 'bi-arrow-clockwise'} me-1 ${isReloading ? 'spinner-animation' : ''}`}></i>
                    {isReloading ? 'Recarregando...' : 'Recarregar'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className='card-body pt-0'>
          {loading ? (
            <Loading />
          ) : (
            <>
              {selectionMode && viewMode === 'list' && (
                <div className='alert alert-info d-flex align-items-center mb-4'>
                  <i className='bi bi-info-circle fs-3 me-3'></i>
                  <div className='flex-grow-1'>
                    <strong>Modo de Seleção:</strong> Selecione as mesas que deseja ativar ou
                    desativar.
                  </div>
                  <button className='btn btn-sm btn-light-primary' onClick={toggleSelectAll}>
                    <i className='bi bi-check-all me-1'></i>
                    {selectedTableIds.length === tables.length ? 'Desmarcar' : 'Selecionar'} Todas
                  </button>
                </div>
              )}
              {viewMode === 'list' ? (
                <TableManagementList
                  tables={tables}
                  onEdit={handleEditTable}
                  onDelete={handleDeleteTable}
                  selectionMode={selectionMode}
                  selectedTableIds={selectedTableIds}
                  onToggleSelection={toggleTableSelection}
                  allocations={allocations}
                />
              ) : (
                <TableManagementMap
                  tables={tables}
                  environmentName={selectedEnvName}
                  onTableClick={handleEditTable}
                  onUpdatePosition={handleUpdateTablePosition}
                  allocations={allocations}
                  availableBookings={availableBookings}
                  onAllocateBooking={handleAllocateBooking}
                  onAllocateBookingMultiple={handleAllocateBookingMultiple}
                  onDeallocateBooking={handleDeallocateBooking}
                  onAllocateWalkIn={handleAllocateWalkIn}
                  onChangeAllocationStatus={handleChangeAllocationStatus}
                  onFetchRelatedAllocations={handleFetchRelatedAllocations}
                  onChangeStatusBulk={handleChangeStatusBulk}
                  onDeallocateBulk={handleDeallocateBulk}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal CRUD Mesa */}
      {showTableModal && tableTypes.length > 0 && (
        <TableCRUDModal
          show={showTableModal}
          onClose={() => {
            console.log('🚪 Modal fechado')
            setShowTableModal(false)
          }}
          onSave={handleSaveTable}
          table={selectedTable || undefined}
          tableTypes={tableTypes}
          action={modalAction}
        />
      )}

      {showTableModal && tableTypes.length === 0 && (
        <div>
          <p>⚠️ Carregando tipos de mesa...</p>
        </div>
      )}

      {/* Modal Criação em Lote */}
      {showBulkModal &&
        selectedEnvironment &&
        (() => {
          const currentEnv = environments.find((e) => e.id === selectedEnvironment)
          return (
            <BulkTableCreationModal
              show={showBulkModal}
              onHide={() => setShowBulkModal(false)}
              onSave={handleSaveBulk}
              tableTypes={tableTypes}
              existingTableNumbers={tables.map((t) => t.tableNumber)}
              existingTables={tables}
              maxTables={currentEnv?.tables || 0}
              maxSeats={currentEnv?.seatsAvailable || 0}
            />
          )
        })()}

      {/* Modal Configurações */}
      {showConfigModal && (
        <TableConfigModal
          show={showConfigModal}
          onClose={() => setShowConfigModal(false)}
          onSave={handleSaveConfig}
          config={config}
          environmentName={selectedEnvName}
        />
      )}

      {/* Modal Distribuição de Mesas */}
      {showDistributionModal && selectedEnvironment && (
        <TableDistributionModal
          show={showDistributionModal}
          onClose={() => setShowDistributionModal(false)}
          environmentId={selectedEnvironment}
          onSuccess={async () => {
            // Recarregar TODOS os dados após distribuição automática
            await loadTables()
            await loadAllocations()
            await loadAvailableBookings()
            // Não mostra Swal aqui pois o modal já mostra
          }}
        />
      )}

      {/* CSS para animação do botão de recarregar */}
      <style>{`
        .spinner-animation {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </>
  )
}

export function TableManagement() {
  return <TableManagementContent />
}
